import React, { useCallback, useMemo } from 'react';
import {
  AccordionItemContent,
  AccordionItemFooter,
  EarningsByMonthContainer,
  EmptyState,
  FooterText,
  FooterTextContainer,
  ItemCard,
  ItemContentContainer,
  LeftContent,
  LeftContentEmpty,
  RightContent,
} from './InfluencerEarningsByMonth.styles';
import Spinner from '../Spinner/Spinner';
import Accordion from '../utils/Accordion/Accordion';
import { format, parse } from 'date-fns';
import { formatPrice } from '../../utils/util';
import { Text14Regular, Text15Regular, Text18Bold } from '../utils/Text/Text.styles';
import { ReactComponent as ImageIcon } from '../../assets/icons/image.svg';
import { ReactComponent as VideoRecorderIcon } from '../../assets/icons/video-recorder.svg';
import { ReactComponent as SaleIcon } from '../../assets/icons/sale.svg';
import { ReactComponent as FilmIcon } from '../../assets/icons/film.svg';
import { ReactComponent as StarIcon } from '../../assets/icons/star.svg';

const ITEM_CONTENT = {
  photo: { name: 'Photos', icon: <ImageIcon /> },
  video: { name: 'Videos', icon: <VideoRecorderIcon color="#97C9F1" /> },
  bundle: { name: 'Bundles', icon: <FilmIcon /> },
  subscription: { name: 'Subscriptions', icon: <StarIcon /> },
  other: { name: 'Other', icon: <SaleIcon /> },
};

const InfluencerEarningsByMonth = ({ monthlyInfluencerEarnings, pending }) => {
  const renderAccordionItemContent = useCallback((data, totalEarningsNet, totalEarningsGross) => {
    return (
      <AccordionItemContent>
        <ItemContentContainer>
          {Object.entries(data).map(([key, value]) => {
            if (key === 'total') return null;
            return (
              <ItemCard key={key}>
                <LeftContent>
                  {ITEM_CONTENT[key].icon}
                  <Text15Regular>{ITEM_CONTENT[key].name}</Text15Regular>
                </LeftContent>
                <RightContent>
                  <FooterTextContainer>
                    <FooterText>Total Net: </FooterText>
                    <Text14Regular>${formatPrice(value.earnings.net)}</Text14Regular>
                  </FooterTextContainer>
                  <FooterTextContainer>
                    <FooterText>Total Gross: </FooterText>
                    <Text18Bold>${formatPrice(value.earnings.gross)}</Text18Bold>
                  </FooterTextContainer>
                </RightContent>
              </ItemCard>
            );
          })}
        </ItemContentContainer>
        <AccordionItemFooter>
          <LeftContentEmpty />
          <FooterTextContainer>
            <FooterText>Total Net: </FooterText>
            <Text14Regular>${formatPrice(totalEarningsNet)}</Text14Regular>
          </FooterTextContainer>
          <FooterTextContainer>
            <FooterText>Total Gross: </FooterText>
            <Text18Bold>${formatPrice(totalEarningsGross)}</Text18Bold>
          </FooterTextContainer>
        </AccordionItemFooter>
      </AccordionItemContent>
    );
  }, []);

  const data = useMemo(() => {
    if (monthlyInfluencerEarnings) {
      return Object.entries(monthlyInfluencerEarnings).map(([key, value]) => {
        const parsedDate = parse(key, 'yyyy-MM', new Date());
        const monthName = format(parsedDate, 'MMMM');
        const { net, gross } = value.total.earnings;

        return {
          title: monthName,
          value: (
            <AccordionItemFooter>
              <FooterTextContainer>
                <FooterText>Total Net: </FooterText>
                <Text14Regular>${formatPrice(net)}</Text14Regular>
              </FooterTextContainer>
              <FooterTextContainer>
                <FooterText>Total Gross: </FooterText>
                <Text18Bold>${formatPrice(gross)}</Text18Bold>
              </FooterTextContainer>
            </AccordionItemFooter>
          ),
          content: renderAccordionItemContent(value, net, gross),
        };
      });
    }

    return [];
  }, [monthlyInfluencerEarnings, renderAccordionItemContent]);

  return (
    <EarningsByMonthContainer>
      {pending ? (
        <Spinner width={150} height={150} />
      ) : data.length ? (
        <Accordion items={data} contentHeight={380} />
      ) : (
        <EmptyState>No monthly earnings yet.</EmptyState>
      )}
    </EarningsByMonthContainer>
  );
};

export default InfluencerEarningsByMonth;
